import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield, faPlus, faEdit, faTrash, faCheck } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';

function RoleManagement({ userId, refreshTrigger, onRefresh }) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });
  const [rolePermissions, setRolePermissions] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'ascending'
  });

  // Add refs to track if we're currently fetching data
  const isFetchingRolesRef = useRef(false);
  const isFetchingPermissionsRef = useRef(false);

  // Memoize the fetchRoles function to prevent unnecessary re-creation
  const fetchRoles = useCallback(async () => {
    // If we're already fetching roles, don't start another fetch
    if (isFetchingRolesRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingRolesRef.current = true;
      setIsLoading(true);

      const response = await api.get(getApiUrl('/api/admin/roles'), {
        params: { userId }
      });

      // Fetch permission counts for each role
      const rolesWithPermCounts = await Promise.all(response.data.map(async (role) => {
        try {
          const permResponse = await api.get(getApiUrl(`/api/admin/roles/${role.id}/permissions`), {
            params: { userId }
          });
          return {
            ...role,
            permission_count: permResponse.data.length
          };
        } catch (error) {
          console.error(`Error fetching permissions for role ${role.id}:`, error);
          return {
            ...role,
            permission_count: 0
          };
        }
      }));

      setRoles(rolesWithPermCounts);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError('Failed to load roles. Please try again later.');
      setIsLoading(false);
    } finally {
      // Reset the fetching flag
      isFetchingRolesRef.current = false;
    }
  }, [userId]);

  // Memoize the fetchPermissions function to prevent unnecessary re-creation
  const fetchPermissions = useCallback(async () => {
    // If we're already fetching permissions, don't start another fetch
    if (isFetchingPermissionsRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingPermissionsRef.current = true;

      const response = await api.get(getApiUrl('/api/admin/permissions'), {
        params: { userId }
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Failed to load permissions. Please try again later.');
    } finally {
      // Reset the fetching flag
      isFetchingPermissionsRef.current = false;
    }
  }, [userId]);

  // Fetch data when the component mounts or refreshTrigger changes
  useEffect(() => {
    // Use a small timeout to debounce multiple rapid calls
    const debounceTimeout = setTimeout(() => {
      fetchRoles();
      fetchPermissions();
    }, 50);

    // Clean up the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [fetchRoles, fetchPermissions, refreshTrigger]);

  const fetchRolePermissions = async (roleId) => {
    try {
      const response = await api.get(getApiUrl(`/api/admin/roles/${roleId}/permissions`), {
        params: { userId }
      });

      // Convert the array of permissions to an object keyed by permission_id
      const permissionsObj = {};
      response.data.forEach(perm => {
        permissionsObj[perm.permission_id] = true;
      });

      return permissionsObj;
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      return {};
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePermissionChange = (permissionId, value) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permissionId]: value
      }
    });
  };

  const handleAddRole = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: {}
    });
    setShowModal(true);
  };

  const handleEditRole = async (role) => {
    setEditingRole(role);

    // Fetch role permissions
    const rolePerms = await fetchRolePermissions(role.id);

    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: rolePerms
    });

    setShowModal(true);
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(getApiUrl(`/api/admin/roles/${roleId}`), {
        params: { userId }
      });

      // Update local state
      setRoles(roles.filter(role => role.id !== roleId));

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('Failed to delete role: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingRole) {
        // Update existing role
        await api.put(getApiUrl(`/api/admin/roles/${editingRole.id}`), {
          ...formData,
          userId
        });

        // Update local state
        setRoles(roles.map(role =>
          role.id === editingRole.id ? { ...role, ...formData } : role
        ));
      } else {
        // Create new role
        const response = await api.post(getApiUrl('/api/admin/roles'), {
          ...formData,
          userId
        });

        // Update local state
        setRoles([...roles, response.data]);
      }

      setShowModal(false);

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error saving role:', error);
      setError('Failed to save role: ' + (error.response?.data?.error || error.message));
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          {/* <div className="loading-spinner"></div> */}
          <p>Loading roles...</p>
        </div>
      </div>
    );
  }

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get the sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      // Handle null or undefined values
      if (a[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? 1 : -1;
      if (b[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? -1 : 1;

      // Handle numeric fields
      if (sortConfig.key === 'user_count' || sortConfig.key === 'permission_count') {
        return sortConfig.direction === 'ascending'
          ? (a[sortConfig.key] || 0) - (b[sortConfig.key] || 0)
          : (b[sortConfig.key] || 0) - (a[sortConfig.key] || 0);
      }



      // Handle string fields
      if (typeof a[sortConfig.key] === 'string' && typeof b[sortConfig.key] === 'string') {
        return sortConfig.direction === 'ascending'
          ? a[sortConfig.key].localeCompare(b[sortConfig.key])
          : b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }

      // Handle other fields
      return sortConfig.direction === 'ascending'
        ? a[sortConfig.key] > b[sortConfig.key] ? 1 : -1
        : a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });
  };

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Apply sorting to roles
  const sortedRoles = getSortedData(roles);

  if (error && !showModal) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchRoles}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2 className="admin-card-title">
          <FontAwesomeIcon icon={faUserShield} className="icon" />
          Role Management
        </h2>
        <button className="btn btn-primary" onClick={handleAddRole}>
          <FontAwesomeIcon icon={faPlus} /> Add Role
        </button>
      </div>

      {roles.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faUserShield} className="empty-state-icon" />
          <p className="empty-state-text">No roles defined yet</p>
          <button className="btn btn-primary" onClick={handleAddRole}>
            <FontAwesomeIcon icon={faPlus} /> Add First Role
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('name')} className="sortable-header">
                  Name{getSortDirectionIndicator('name')}
                </th>
                <th onClick={() => requestSort('description')} className="sortable-header">
                  Description{getSortDirectionIndicator('description')}
                </th>
                <th onClick={() => requestSort('user_count')} className="sortable-header">
                  Users{getSortDirectionIndicator('user_count')}
                </th>
                <th onClick={() => requestSort('permission_count')} className="sortable-header">
                  Permissions{getSortDirectionIndicator('permission_count')}
                </th>

                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRoles.map(role => (
                <tr key={role.id}>
                  <td>{role.name}</td>
                  <td>{role.description || '-'}</td>
                  <td>{role.user_count || 0}</td>
                  <td>{role.permission_count || 0}</td>

                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditRole(role)}
                        title="Edit Role"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteRole(role.id)}
                        title="Delete Role"
                        disabled={role.user_count > 0}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingRole ? 'Edit Role' : 'Add New Role'}
              </h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-group">
                  <label htmlFor="name">Role Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>



                <div className="form-group">
                  <label>Permissions</label>
                  <div className="permissions-list">
                    {permissions.length === 0 ? (
                      <p>No permissions available. Please create permissions first.</p>
                    ) : (
                      <>
                        {/* Channel Access Permissions */}
                        {permissions.some(p => p.resource_type === 'channel') && (
                          <div className="permission-category">
                            <h4 className="permission-category-title">📺 Channel Access</h4>
                            <p className="permission-category-desc">Control which advertising channels this role can access</p>
                            <div className="permission-category-items">
                              {permissions
                                .filter(p => p.resource_type === 'channel')
                                .map(permission => (
                                  <div key={permission.id} className="permission-item channel-permission">
                                    <label className="checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={!!formData.permissions[permission.id]}
                                        onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                                      />
                                      <span className="permission-name">{permission.name}</span>
                                    </label>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Other Permissions */}
                        {permissions.some(p => p.resource_type !== 'channel') && (
                          <div className="permission-category">
                            <h4 className="permission-category-title">⚙️ General Permissions</h4>
                            <div className="permission-category-items">
                              {permissions
                                .filter(p => p.resource_type !== 'channel')
                                .map(permission => (
                                  <div key={permission.id} className="permission-item">
                                    <label className="checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={!!formData.permissions[permission.id]}
                                        onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                                      />
                                      <span className="permission-name">{permission.name}</span>
                                    </label>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {error && showModal && (
                  <div className="error-message">{error}</div>
                )}

                <div className="modal-footer">
                  <button type="submit" className="btn btn-success">
                    {editingRole ? 'Update Role' : 'Create Role'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleManagement;
